import { apiUrl } from "../../ApiManager/AxiosHeaders";
import { getApiErrorMessage as formatApiError } from "../../../utilis/apiErrorMessage";
import { mapWithConcurrency } from "../../../utilis/mapWithConcurrency";
import {
  buildFilterParams,
  getDiscontinuedStatusMeta,
  getReactivateStatusMeta,
  getProviderCategory,
  resolveApplicationAgentDisplay,
  resolveApplicationBranchDisplay,
} from "./commissionsConstants";
import { buildInvoiceCreateRequestBody } from "../../../utilis/invoiceCreateHelpers";
import { buildMultipartFormDataRequestConfig } from "../../../utilis/financeInvoicePaymentHelpers";
import {
  extractFinanceNotesList,
  extractClientNotesList,
  normalizeFinanceInvoiceRecord,
} from "../../../utilis/invoiceEditHelpers";
import { resolveInvoiceRecordPayableTotals } from "../../../utilis/invoiceLineItemHelpers";
import { extractBonusesFromResponse } from "./detail/commissionPlanUtils";
import {
  extractBonusInstallments,
  extractCommissionInstallments,
} from "./detail/commissionInstallmentUtils";
import { buildProviderDisplayProfileFromApi } from "../../../utilis/applicationCostHelpers";

export async function fetchApplications(filters, pagination) {
  const params = buildFilterParams(filters, pagination);
  const response = await apiUrl.get("/getAllApplicationsWithFullHistory", {
    params,
  });
  if (response?.status) {
    return {
      raw: response?.data,
      data: response?.data?.data || [],
      pagination: response?.data?.pagination || {},
    };
  }
  return { raw: response?.data ?? null, data: [], pagination: {} };
}

const FETCH_ALL_APPLICATIONS_MAX = 2000;
const FETCH_ALL_APPLICATIONS_PAGE_SIZE = 200;

/**
 * Load every row matching current commission filters (all API pages).
 * Used for provider bulk inquiry selection.
 */
export async function fetchAllApplicationsMatchingFilters(filters) {
  const first = await fetchApplications(filters, {
    currentPage: 1,
    perPage: FETCH_ALL_APPLICATIONS_PAGE_SIZE,
  });
  const total = Number(first.pagination?.total ?? 0);
  if (!total) {
    return [];
  }
  if (total > FETCH_ALL_APPLICATIONS_MAX) {
    throw new Error(
      `Too many students (${total}). Narrow filters (max ${FETCH_ALL_APPLICATIONS_MAX}).`,
    );
  }

  const all = [...(first.data || [])];
  const lastPage = Number(
    first.pagination?.lastPage ??
      Math.max(1, Math.ceil(total / FETCH_ALL_APPLICATIONS_PAGE_SIZE)),
  );

  for (let page = 2; page <= lastPage; page += 1) {
    const result = await fetchApplications(filters, {
      currentPage: page,
      perPage: FETCH_ALL_APPLICATIONS_PAGE_SIZE,
    });
    all.push(...(result.data || []));
  }

  return all;
}

/**
 * GET /commission-reconciliation
 * Read-only recap of the money recorded against an application plus the derived
 * workflow state. Returns null when there is nothing to reconcile.
 */
export async function fetchCommissionReconciliation({
  applicationId,
  applicationType = "education",
} = {}) {
  if (!applicationId) return null;

  try {
    const response = await apiUrl.get("/commission-reconciliation", {
      params: {
        application_id: applicationId,
        application_type: applicationType,
      },
    });

    if (response?.data?.status && response?.data?.data) {
      return response.data.data;
    }

    return null;
  } catch {
    // The recap is advisory — never block the detail page because it failed.
    return null;
  }
}

/** GET /commission-status-rules */
export async function fetchCommissionStatusRules(applicationType) {
  const response = await apiUrl.get("/commission-status-rules", {
    params: { application_type: applicationType || undefined },
  });

  const payload = response?.data;

  if (payload?.status === false) {
    throw new Error(payload?.message || "Failed to load commission rules.");
  }

  return {
    rules: Array.isArray(payload?.data) ? payload.data : [],
    options: payload?.options || {},
    counts: payload?.counts || {},
  };
}

/** POST /commission-status-rules/{id}/update */
export async function updateCommissionStatusRule(ruleId, payload) {
  if (!ruleId) throw new Error("Rule id is required.");

  try {
    const response = await apiUrl.post(
      `/commission-status-rules/${ruleId}/update`,
      payload,
    );

    if (response?.data?.status) {
      return response.data;
    }

    throw new Error(response?.data?.message || "Failed to update rule.");
  } catch (error) {
    throw new Error(
      error?.response?.data?.message ||
        error?.message ||
        "Failed to update rule.",
    );
  }
}

/** GET /student-provider-course-details */
export async function fetchStudentProviderCourseDetails({
  studentId,
  appId,
  providerId,
  courseId,
}) {
  try {
    const response = await apiUrl.get("/student-provider-course-details", {
      params: {
        student_id: studentId,
        app_id: appId,
        provider_id: providerId,
        course_id: courseId,
      },
    });

    if (response?.data?.status && response?.data?.data) {
      return response.data.data;
    }

    const message =
      response?.data?.message || "Failed to fetch commission details.";
    throw new Error(message);
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch commission details.";
    throw new Error(message);
  }
}

/** GET /getApplicationFullDetails */
export async function fetchApplicationFullDetails({
  applicationId,
  type,
  clientId,
  providerId,
  courseId,
  applicationCourseId,
}) {
  try {
    const response = await apiUrl.get("/getApplicationFullDetails", {
      params: {
        application_id: applicationId,
        type,
        client_id: clientId || undefined,
        provider_id: providerId || undefined,
        course_id: courseId || undefined,
        application_course_id: applicationCourseId || undefined,
      },
    });

    if (response?.data?.status && response?.data?.data != null) {
      return response.data.data;
    }

    // Some APIs return the record at the root of data
    if (
      response?.data &&
      response?.data?.status !== false &&
      !response?.data?.message
    ) {
      return response.data?.data ?? response.data;
    }

    const message =
      response?.data?.message || "Failed to fetch application full details.";
    throw new Error(message);
  } catch (error) {
    if (error instanceof Error && !error.isAxiosError && !error.response) {
      throw error;
    }
    throw new Error(
      formatApiError(error) || "Failed to fetch application full details.",
    );
  }
}

/** POST /new-commissions — create a commission plan */
export async function createNewCommissionPlan(payload) {
  try {
    const response = await apiUrl.post("/new-commissions", payload);

    if (response?.data?.status || response?.data?.success || response?.status) {
      return response.data;
    }

    const message =
      response?.data?.message || "Failed to create commission plan.";
    throw new Error(message);
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to create commission plan.";
    throw new Error(message);
  }
}

/** POST /new-commissions/{id}/update — update a commission plan */
export async function updateNewCommissionPlan(planId, payload) {
  try {
    const response = await apiUrl.post(
      `/new-commissions/${planId}/update`,
      payload,
    );

    if (response?.data?.status || response?.data?.success || response?.status) {
      return response.data;
    }

    const message =
      response?.data?.message || "Failed to update commission plan.";
    throw new Error(message);
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to update commission plan.";
    throw new Error(message);
  }
}

function pickPlanFromPayload(payload) {
  const data = payload?.data ?? payload;
  if (!data) return null;

  if (Array.isArray(data)) return data[0] || null;
  if (Array.isArray(data?.data)) return data.data[0] || null;
  if (data?.new_commission_plan) return data.new_commission_plan;
  if (data?.commission_plan) return data.commission_plan;
  if (Array.isArray(data?.new_commission_plans)) {
    return data.new_commission_plans[0] || null;
  }
  if (
    typeof data === "object" &&
    (data.id != null || data.application_id != null)
  ) {
    return data;
  }
  return null;
}

function normalizeCommissionPlanSummaryPayload(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (payload.summary && typeof payload.summary === "object") {
    return payload.summary;
  }
  if (
    payload.tuition ||
    payload.commission ||
    payload.totals ||
    payload.bonus
  ) {
    return payload;
  }
  return null;
}

function buildPlanResult(plan, data) {
  if (!plan) {
    return { plan: null, bonuses: [], installments: [], raw: data ?? null };
  }

  const extractedBonuses = extractBonusesFromResponse(data);
  const bonusList = extractedBonuses.length
    ? extractedBonuses
    : extractBonusesFromResponse(plan);

  const rawBonusRows = [
    ...(Array.isArray(data?.bonuses) ? data.bonuses : []),
    ...(Array.isArray(data?.new_commission_bonuses)
      ? data.new_commission_bonuses
      : []),
    ...(Array.isArray(plan?.bonuses) ? plan.bonuses : []),
    ...(Array.isArray(plan?.new_commission_bonuses)
      ? plan.new_commission_bonuses
      : []),
  ];

  const bonuses = bonusList.map((bonus) => {
    const rawBonus =
      rawBonusRows.find((row) => String(row.id) === String(bonus.id)) || null;
    return {
      ...bonus,
      installments: extractBonusInstallments(rawBonus || bonus),
    };
  });

  const extractedInstallments = extractCommissionInstallments(data);
  const installments = extractedInstallments.length
    ? extractedInstallments
    : extractCommissionInstallments(plan);

  return {
    plan,
    bonuses,
    installments,
    summary: normalizeCommissionPlanSummaryPayload(
      data?.summary ?? plan?.summary ?? null,
    ),
    raw: data ?? plan,
  };
}

/**
 * GET /new-commissions — list/filter saved commission plans.
 * Returns `{ plan, bonuses }` for the matching application course.
 */
export async function fetchNewCommissionPlans({
  applicationId,
  applicationCourseId,
  clientId,
  providerId,
  courseId,
  planId,
} = {}) {
  try {
    const response = await apiUrl.get("/new-commissions", {
      params: {
        id: planId || undefined,
        application_id: applicationId || undefined,
        application_course_id: applicationCourseId || undefined,
        client_id: clientId || undefined,
        provider_id: providerId || undefined,
        course_id: courseId || undefined,
      },
    });

    const payload = response?.data;
    if (payload?.status === false) {
      return { plan: null, bonuses: [], installments: [], raw: null };
    }

    const data = payload?.data ?? payload;
    let plan = pickPlanFromPayload(payload);

    // Prefer exact application_course match when list returned
    if (Array.isArray(data) && applicationCourseId != null) {
      const match = data.find(
        (row) =>
          String(row.application_course_id) === String(applicationCourseId) &&
          (applicationId == null ||
            String(row.application_id) === String(applicationId)),
      );
      if (match) plan = match;
    } else if (Array.isArray(data?.data) && applicationCourseId != null) {
      const match = data.data.find(
        (row) =>
          String(row.application_course_id) === String(applicationCourseId) &&
          (applicationId == null ||
            String(row.application_id) === String(applicationId)),
      );
      if (match) plan = match;
    }

    return buildPlanResult(plan, data);
  } catch (error) {
    if (error?.response?.status === 404) {
      return { plan: null, bonuses: [], installments: [], raw: null };
    }
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch commission plans.";
    throw new Error(message);
  }
}

/**
 * GET /new-commissions/{id} — single commission plan (+ bonuses).
 */
export async function fetchNewCommissionPlanById(planId) {
  if (!planId) return { plan: null, bonuses: [], installments: [], raw: null };

  try {
    const response = await apiUrl.get(`/new-commissions/${planId}`);
    const payload = response?.data;

    if (payload?.status === false) {
      return { plan: null, bonuses: [], installments: [], raw: null };
    }

    const data = payload?.data ?? payload;
    const plan = pickPlanFromPayload(payload);
    return buildPlanResult(plan, data);
  } catch (error) {
    if (error?.response?.status === 404) {
      return { plan: null, bonuses: [], installments: [], raw: null };
    }
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch commission plan.";
    throw new Error(message);
  }
}

/**
 * Resolve saved plan:
 * 1) if planId known → GET /new-commissions/{id}
 * 2) else → GET /new-commissions (filtered index)
 * 3) if index finds an id → GET /new-commissions/{id} for full details
 */
export async function fetchNewCommissionPlan({
  planId,
  applicationId,
  applicationCourseId,
  clientId,
  providerId,
  courseId,
} = {}) {
  if (planId) {
    return fetchNewCommissionPlanById(planId);
  }

  const listed = await fetchNewCommissionPlans({
    applicationId,
    applicationCourseId,
    clientId,
    providerId,
    courseId,
  });

  const listedId = listed?.plan?.id;
  if (listedId) {
    try {
      const detailed = await fetchNewCommissionPlanById(listedId);
      if (detailed?.plan) return detailed;
    } catch (error) {
      // Fall back to index row if show fails
      console.error(error);
    }
  }

  return listed;
}

export function extractCreatedPlanId(createResponse) {
  if (!createResponse || typeof createResponse !== "object") return null;
  const data = createResponse.data ?? createResponse;
  return (
    data?.id ??
    data?.plan_id ??
    data?.commission_plan_id ??
    data?.new_commission_plan?.id ??
    data?.commission_plan?.id ??
    createResponse?.id ??
    null
  );
}

/** POST /new-commissions/{planId}/bonuses */
export async function createNewCommissionBonus(planId, payload) {
  try {
    const response = await apiUrl.post(
      `/new-commissions/${planId}/bonuses`,
      payload,
    );

    if (response?.data?.status || response?.data?.success || response?.status) {
      return response.data;
    }

    const message =
      response?.data?.message || "Failed to create commission bonus.";
    throw new Error(message);
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to create commission bonus.";
    throw new Error(message);
  }
}

/** POST /new-commission-bonuses/{id}/update */
export async function updateNewCommissionBonus(bonusId, payload) {
  try {
    const response = await apiUrl.post(
      `/new-commission-bonuses/${bonusId}/update`,
      payload,
    );

    if (response?.data?.status || response?.data?.success || response?.status) {
      return response.data;
    }

    const message =
      response?.data?.message || "Failed to update commission bonus.";
    throw new Error(message);
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to update commission bonus.";
    throw new Error(message);
  }
}

export async function deleteNewCommissionBonus(bonusId) {
  try {
    const response = await apiUrl.delete(`/new-commission-bonuses/${bonusId}`);

    if (response?.data?.status || response?.data?.success || response?.status) {
      return response.data;
    }

    const message =
      response?.data?.message || "Failed to delete commission bonus.";
    throw new Error(message);
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to delete commission bonus.";

    throw new Error(message);
  }
}

/** POST /new-commissions/{planId}/installments */
export async function createCommissionInstallments(planId, payload) {
  try {
    const response = await apiUrl.post(
      `/new-commissions/${planId}/installments`,
      payload,
    );

    if (response?.data?.status || response?.data?.success || response?.status) {
      return response.data;
    }

    const message =
      response?.data?.message || "Failed to create commission installments.";
    throw new Error(message);
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to create commission installments.";
    throw new Error(message);
  }
}

/** POST /new-commission-installments/{id}/update */
export async function updateCommissionInstallment(installmentId, payload) {
  try {
    const response = await apiUrl.post(
      `/new-commission-installments/${installmentId}/update`,
      payload,
    );

    if (response?.data?.status || response?.data?.success || response?.status) {
      return response.data;
    }

    const message =
      response?.data?.message || "Failed to update commission installment.";
    throw new Error(message);
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to update commission installment.";
    throw new Error(message);
  }
}

/** POST /new-commission-bonuses/{bonusId}/installments */
export async function createBonusInstallments(bonusId, payload) {
  try {
    const response = await apiUrl.post(
      `/new-commission-bonuses/${bonusId}/installments`,
      payload,
    );

    if (response?.data?.status || response?.data?.success || response?.status) {
      return response.data;
    }

    const message =
      response?.data?.message || "Failed to create bonus installments.";
    throw new Error(message);
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to create bonus installments.";
    throw new Error(message);
  }
}

/** POST /new-commission-bonus-installments/{id}/update */
export async function updateBonusInstallment(installmentId, payload) {
  try {
    const response = await apiUrl.post(
      `/new-commission-bonus-installments/${installmentId}/update`,
      payload,
    );

    if (response?.data?.status || response?.data?.success || response?.status) {
      return response.data;
    }

    const message =
      response?.data?.message || "Failed to update bonus installment.";
    throw new Error(message);
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to update bonus installment.";
    throw new Error(message);
  }
}

/** POST /api/v1/new-commission-installments/{id}/delete */
export async function deleteCommissionInstallment(installmentId) {
  if (installmentId == null || installmentId === "") {
    throw new Error("Installment id is required.");
  }

  try {
    const response = await apiUrl.post(
      `/new-commission-installments/${installmentId}/delete`,
      {},
    );

    if (
      response?.data?.status ||
      response?.data?.success ||
      (response?.status >= 200 && response?.status < 300)
    ) {
      return response.data;
    }

    const message =
      response?.data?.message || "Failed to delete commission installment.";
    throw new Error(message);
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to delete commission installment.";
    throw new Error(message);
  }
}

/** Delete every commission installment (calls delete per row id). */
export async function deleteAllCommissionInstallments(installments = []) {
  const ids = (Array.isArray(installments) ? installments : [])
    .map((row) => row?.id)
    .filter((id) => id != null && id !== "");

  if (!ids.length) return { deleted: 0 };

  await mapWithConcurrency(ids, (id) => deleteCommissionInstallment(id));
  return { deleted: ids.length };
}

/** POST /api/v1/new-commission-bonus-installments/{id}/delete */
export async function deleteBonusInstallment(installmentId) {
  if (installmentId == null || installmentId === "") {
    throw new Error("Bonus installment id is required.");
  }

  try {
    const response = await apiUrl.post(
      `/new-commission-bonus-installments/${installmentId}/delete`,
      {},
    );

    if (
      response?.data?.status ||
      response?.data?.success ||
      (response?.status >= 200 && response?.status < 300)
    ) {
      return response.data;
    }

    const message =
      response?.data?.message || "Failed to delete bonus installment.";
    throw new Error(message);
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to delete bonus installment.";
    throw new Error(message);
  }
}

/** Delete every bonus installment (calls delete per row id). */
export async function deleteAllBonusInstallments(installments = []) {
  const ids = (Array.isArray(installments) ? installments : [])
    .map((row) => row?.id)
    .filter((id) => id != null && id !== "");

  if (!ids.length) return { deleted: 0 };

  await mapWithConcurrency(ids, (id) => deleteBonusInstallment(id));
  return { deleted: ids.length };
}

function isApiSuccess(response) {
  const payload = response?.data;
  if (payload?.status === false || payload?.success === false) return false;
  if (payload?.status || payload?.success || response?.status) return true;
  return Boolean(payload);
}

function getApiErrorMessage(error, fallback) {
  if (error?.isAxiosError || error?.response?.status || error?.code === "ERR_NETWORK") {
    return formatApiError(error) || fallback;
  }

  return (
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.response?.data?.error ||
    error?.data?.error ||
    error?.message ||
    fallback
  );
}

function unwrapApiData(payload) {
  if (payload == null) return null;
  if (payload?.data !== undefined && typeof payload.data === "object") {
    return payload.data;
  }
  return payload;
}

/** Normalize list payloads from commission invoice endpoints. */
export function extractCommissionInvoiceList(payload) {
  const root = unwrapApiData(payload) ?? payload;
  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.invoices)) return root.invoices;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root?.data?.data)) return root.data.data;
  if (Array.isArray(root?.new_commission_invoices)) {
    return root.new_commission_invoices;
  }
  if (Array.isArray(payload?.invoices)) return payload.invoices;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
}

/** Normalize a commission invoice row for UI.
 * List API shape:
 * { invoice: {...}, linked_installments, linked_bonus_installments, installment_ids, bonus_installment_ids }
 */
function mapCommissionInvoiceStatus(status) {
  const value = String(status || "pending").toLowerCase().trim();
  if (value === "overdue" || value === "issued") return "awaiting_payment";
  if (value === "void") return "void";
  if (value === "pending" || value === "quotation" || value === "accepted") {
    return "draft";
  }
  if (value === "approved") return "awaiting_payment";
  return value || "draft";
}

export function normalizeCommissionInvoice(raw = {}) {
  if (!raw || typeof raw !== "object") return null;

  const nestedInvoice =
    raw.invoice &&
    typeof raw.invoice === "object" &&
    !Array.isArray(raw.invoice)
      ? raw.invoice
      : null;
  const record = nestedInvoice || raw;

  const id =
    record.id ?? raw.id ?? raw.invoice_id ?? raw.commission_invoice_id ?? null;

  const financeInvoiceId =
    record.id ??
    raw.finance_invoice_id ??
    raw.financeInvoiceId ??
    raw.finance_invoice?.id ??
    null;

  const installmentIds = [
    ...(Array.isArray(raw.installment_ids) ? raw.installment_ids : []),
    ...(Array.isArray(raw.commission_installment_ids)
      ? raw.commission_installment_ids
      : []),
    ...(Array.isArray(raw.commission_installments)
      ? raw.commission_installments.map((row) => row?.id ?? row).filter(Boolean)
      : []),
    ...(Array.isArray(raw.linked_installments)
      ? raw.linked_installments.map((row) => row?.id).filter(Boolean)
      : []),
  ];

  const bonusIds = [
    ...(Array.isArray(raw.bonus_installment_ids)
      ? raw.bonus_installment_ids
      : []),
    ...(Array.isArray(raw.bonus_installments)
      ? raw.bonus_installments.map((row) => row?.id ?? row).filter(Boolean)
      : []),
    ...(Array.isArray(raw.linked_bonus_installments)
      ? raw.linked_bonus_installments.map((row) => row?.id).filter(Boolean)
      : []),
  ];

  const invoiceType = String(
    record.invoice_type || raw.invoice_type || raw.invoiceType || "",
  ).toLowerCase();
  const hasBonus = bonusIds.length > 0;
  const hasCommission = installmentIds.length > 0;
  let typeBadge = "";
  if (invoiceType === "bonus" || (hasBonus && !hasCommission)) {
    typeBadge = "Bonus";
  } else if (
    invoiceType === "commission_bonus" ||
    (hasBonus && hasCommission)
  ) {
    typeBadge = "Commission + Bonus";
  } else if (
    invoiceType === "commission" ||
    hasCommission ||
    String(
      record.invoice_source_module || raw.invoice_source_module || "",
    ).toLowerCase() === "new_commission"
  ) {
    typeBadge = "Commission";
  }

  const invoiceNumber =
    record.invoice_number ||
    record.invoiceNumber ||
    raw.invoice_number ||
    raw.invoiceNumber ||
    null;

  const notes = Array.isArray(record.notes)
    ? record.notes
    : Array.isArray(raw.notes)
      ? raw.notes
      : Array.isArray(raw.invoice_notes)
        ? raw.invoice_notes
        : [];

  const payable = resolveInvoiceRecordPayableTotals({
    ...record,
    items: record.items ?? raw.items,
    line_items: record.line_items ?? raw.line_items,
    totals: record.totals ?? raw.totals,
    paid_amount: record.paid_amount ?? raw.paid_amount,
    amount_due: record.amount_due ?? raw.amount_due,
    total_amount:
      record.total_amount ??
      raw.total_amount ??
      raw.amount ??
      raw.invoice_amount,
    tax_type: record.tax_type ?? raw.tax_type ?? record.totals?.tax_type,
    tax_total:
      record.tax_total ?? raw.tax_total ?? record.totals?.tax_total,
  });

  return {
    id,
    invoiceNumber: invoiceNumber || (id != null ? `INV-${id}` : "—"),
    notes,
    status: mapCommissionInvoiceStatus(record.status || raw.status),
    voidedFromStatus:
      record.voided_from_status ??
      raw.voided_from_status ??
      raw.voidedFromStatus ??
      null,
    amount: payable.total_amount,
    paidAmount: payable.paid_amount,
    amountDue: payable.amount_due,
    discountTotal: Number(
      record.discount_total ?? raw.discount_total ?? raw.discountTotal ?? 0,
    ),
    currency: record.currency || raw.currency || "AUD",
    remarks: record.reference || raw.remarks || raw.note || "",
    reference: record.reference || raw.reference || "",
    invoiceDate:
      record.issue_date ||
      record.invoice_date ||
      raw.invoice_date ||
      raw.invoiceDate ||
      raw.created_at ||
      null,
    dueDate: record.due_date || raw.due_date || raw.dueDate || null,
    expectedPaymentDate:
      record.expected_payment_date ||
      raw.expected_payment_date ||
      raw.expectedPaymentDate ||
      null,
    expectedPaymentNote:
      record.expected_payment_note ||
      raw.expected_payment_note ||
      raw.expectedPaymentNote ||
      "",
    invoiceType: invoiceType || null,
    invoiceSourceModule:
      record.invoice_source_module ||
      raw.invoice_source_module ||
      raw.invoiceSourceModule ||
      null,
    invoiceSourceId:
      record.invoice_source_id ??
      raw.invoice_source_id ??
      raw.invoiceSourceId ??
      null,
    clientId: record.client_id ?? raw.client_id ?? null,
    typeBadge,
    financeInvoiceId,
    commissionInstallmentIds: [...new Set(installmentIds.map(String))],
    bonusInstallmentIds: [...new Set(bonusIds.map(String))],
    linkedInstallments: Array.isArray(raw.linked_installments)
      ? raw.linked_installments
      : [],
    linkedBonusInstallments: Array.isArray(raw.linked_bonus_installments)
      ? raw.linked_bonus_installments
      : [],
    createdAt: record.created_at || raw.created_at || raw.createdAt || null,
    raw: record,
    wrapper: nestedInvoice ? raw : null,
  };
}

/**
 * GET /new-commissions/{id}/summary
 * Plan totals / invoice readiness snapshot.
 */
export async function fetchCommissionSummary(planId) {
  if (!planId) return null;
  try {
    const response = await apiUrl.get(`/new-commissions/${planId}/summary`);
    if (!isApiSuccess(response)) {
      throw new Error(
        response?.data?.message || "Failed to fetch commission summary.",
      );
    }
    return normalizeCommissionPlanSummaryPayload(
      unwrapApiData(response?.data) ?? response?.data ?? null,
    );
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to fetch commission summary."),
    );
  }
}

export function mapCommissionInvoiceListRowForFinanceTab(row = {}) {
  const totals = row.totals || {};

  return {
    ...row,
    id: row.id ?? null,
    invoice_number: row.invoice_number || row.invoiceNumber || "",
    status: row.status || "draft",
    payment_status: row.payment_status || row.status || "draft",
    client_id: row.client_id ?? row.clientId ?? null,
    provider_name: row.provider_name || row.providerName || "",
    provider_id: row.provider_id ?? row.providerId ?? null,
    student_name: row.student_name || row.studentName || "",
    client_name:
      row.client_name ||
      row.clientName ||
      row.student_name ||
      row.studentName ||
      row.client_snapshot?.full_name ||
      row.client_snapshot?.name ||
      "",
    invoice_source_module: row.invoice_source_module || "new_commission",
    invoice_type: row.invoice_type || row.invoiceType || null,
    total_amount: totals.total_amount ?? row.total_amount ?? row.totalAmount ?? 0,
    paid_amount: totals.paid_amount ?? row.paid_amount ?? row.paidAmount ?? 0,
    amount_due: totals.amount_due ?? row.amount_due ?? row.amountDue ?? 0,
    discount_total:
      totals.discount_total ?? row.discount_total ?? row.discountTotal ?? 0,
    currency: row.currency || totals.currency || "AUD",
    issue_date: row.issue_date || row.issueDate || null,
    due_date: row.due_date || row.dueDate || null,
    expected_payment_date:
      row.expected_payment_date || row.expectedPaymentDate || null,
    expected_payment_note:
      row.expected_payment_note || row.expectedPaymentNote || "",
    client_snapshot: row.client_snapshot || null,
    commission_plan_id: row.commission_plan_id ?? row.commissionPlanId ?? null,
    line_items: row.line_items || row.items || [],
  };
}

function normalizeCommissionInvoiceListPagination(response) {
  const meta =
    response?.data?.pagination ||
    response?.data?.meta ||
    response?.data?.data?.pagination ||
    null;

  if (!meta || typeof meta !== "object") {
    return null;
  }

  return {
    currentPage: Number(meta.current_page ?? meta.currentPage ?? 1) || 1,
    lastPage: Number(meta.last_page ?? meta.lastPage ?? 1) || 1,
    perPage: Number(meta.per_page ?? meta.perPage ?? 15) || 15,
    total: Number(meta.total ?? 0) || 0,
    from: Number(meta.from ?? 0) || 0,
    to: Number(meta.to ?? 0) || 0,
  };
}

/**
 * GET /new-commission-invoices
 * All commission-module invoices (paginated).
 */
export async function listAllCommissionInvoices(params = {}) {
  const response = await apiUrl.get("/new-commission-invoices", { params });
  const payload = response?.data;

  if (payload?.status === false || payload?.success === false) {
    throw new Error(
      getApiErrorMessage(response, "Failed to load commission invoices."),
    );
  }

  const rows = extractCommissionInvoiceList(payload).map(
    mapCommissionInvoiceListRowForFinanceTab,
  );

  return {
    items: rows,
    pagination: normalizeCommissionInvoiceListPagination(response),
  };
}

/**
 * GET /new-commissions/{planId}/invoices
 * Invoices created for this commission plan.
 */
export async function listCommissionInvoices(planId) {
  if (!planId) return [];
  try {
    const response = await apiUrl.get(`/new-commissions/${planId}/invoices`);
    const payload = response?.data;

    if (response?.status === 404) return [];
    if (payload?.status === false || payload?.success === false) {
      // Empty collection is fine; real API errors have a message.
      const message = payload?.message || payload?.error || "";
      if (message && !/not found|no invoice/i.test(String(message))) {
        throw new Error(message);
      }
      return [];
    }

    return extractCommissionInvoiceList(payload)
      .map(normalizeCommissionInvoice)
      .filter(Boolean);
  } catch (error) {
    if (error?.response?.status === 404) return [];
    if (error?.message && !error?.response) throw error;
    throw new Error(
      getApiErrorMessage(error, "Failed to load commission invoices."),
    );
  }
}

/**
 * GET /getFinanceNotes/{clientId}
 * Client financial notes (same source as ViewClientDetail).
 */
export async function fetchClientFinanceNotes(clientId) {
  if (!clientId) return [];
  try {
    const response = await apiUrl.get(
      `getFinanceNotes/${clientId}?page=1&perPage=500`,
    );
    return extractFinanceNotesList(response);
  } catch {
    return [];
  }
}

/**
 * GET /getClientNotes/{clientId}
 * All client notes (same source as ViewClientDetail general tab).
 */
export async function fetchClientAllNotes(clientId) {
  if (!clientId) return [];
  try {
    const response = await apiUrl.get(
      `getClientNotes/${clientId}?page=1&perPage=500`,
    );
    return extractClientNotesList(response);
  } catch {
    return [];
  }
}

/**
 * POST /new-commissions/{planId}/invoices
 * Create commission/bonus invoice (backend also creates finance invoice).
 * Keep branch/branding/line items/payment and tuition flags.
 */
export async function createCommissionInvoice(planId, payload) {
  if (!planId) throw new Error("Commission plan is required.");
  try {
    const { body, isFormData } = buildInvoiceCreateRequestBody(payload);
    const response = await apiUrl.post(
      `/new-commissions/${planId}/invoices`,
      body,
      ...(isFormData ? [buildMultipartFormDataRequestConfig()] : []),
    );
    if (!isApiSuccess(response)) {
      throw new Error(
        response?.data?.message || "Failed to create commission invoice.",
      );
    }
    const data = unwrapApiData(response?.data) ?? response?.data;
    return {
      raw: response?.data,
      invoice: normalizeCommissionInvoice(
        data?.invoice || data?.commission_invoice || data,
      ),
    };
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to create commission invoice."),
    );
  }
}

/** POST /new-commission-installments/{id}/mark-tuition-paid */
export async function markCommissionInstallmentTuitionPaid(
  installmentId,
  payload,
) {
  if (!installmentId) throw new Error("Installment id is required.");
  try {
    const response = await apiUrl.post(
      `/new-commission-installments/${installmentId}/mark-tuition-paid`,
      payload,
    );
    if (!isApiSuccess(response)) {
      throw new Error(
        response?.data?.message || "Failed to update tuition paid status.",
      );
    }
    return response?.data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to update tuition paid status."),
    );
  }
}

/** POST /new-commission-installments/mark-tuition-paid-bulk */
export async function markCommissionInstallmentsTuitionPaidBulk(payload) {
  try {
    const response = await apiUrl.post(
      `/new-commission-installments/mark-tuition-paid-bulk`,
      payload,
    );
    if (!isApiSuccess(response)) {
      throw new Error(
        response?.data?.message || "Failed to update tuition paid status.",
      );
    }
    return response?.data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to update tuition paid status."),
    );
  }
}

/** GET /new-commission-invoices/{invoiceId} */
export async function fetchCommissionInvoice(invoiceId) {
  if (!invoiceId) return null;
  try {
    const response = await apiUrl.get(`/new-commission-invoices/${invoiceId}`);
    if (!isApiSuccess(response)) {
      throw new Error(
        response?.data?.message || "Failed to load commission invoice.",
      );
    }
    const data = unwrapApiData(response?.data) ?? response?.data;
    // Support both { invoice, linked_*, installment_ids } and flat invoice payloads.
    const nested =
      data?.invoice && typeof data.invoice === "object" ? data.invoice : null;
    const record = nested || data?.commission_invoice || data;
    const wrapper = nested ? data : record;

    const notes = Array.isArray(record?.notes)
      ? record.notes
      : Array.isArray(data?.notes)
        ? data.notes
        : [];

    const normalized = normalizeCommissionInvoice(
      nested ? { ...data, invoice: record } : record,
    );

    const commissionInstallmentIds = [
      ...(normalized?.commissionInstallmentIds || []),
      ...(Array.isArray(wrapper?.installment_ids)
        ? wrapper.installment_ids.map(String)
        : []),
      ...(Array.isArray(wrapper?.linked_installments)
        ? wrapper.linked_installments
            .map((row) => String(row?.id))
            .filter(Boolean)
        : []),
    ];
    const bonusInstallmentIds = [
      ...(normalized?.bonusInstallmentIds || []),
      ...(Array.isArray(wrapper?.bonus_installment_ids)
        ? wrapper.bonus_installment_ids.map(String)
        : []),
      ...(Array.isArray(wrapper?.linked_bonus_installments)
        ? wrapper.linked_bonus_installments
            .map((row) => String(row?.id))
            .filter(Boolean)
        : []),
    ];

    return {
      invoice: {
        ...normalized,
        commissionInstallmentIds: [...new Set(commissionInstallmentIds)],
        bonusInstallmentIds: [...new Set(bonusInstallmentIds)],
      },
      raw: record,
      wrapper,
      notes,
      commissionInstallmentIds: [...new Set(commissionInstallmentIds)],
      bonusInstallmentIds: [...new Set(bonusInstallmentIds)],
      linkedInstallments: Array.isArray(wrapper?.linked_installments)
        ? wrapper.linked_installments
        : normalized?.linkedInstallments || [],
      linkedBonusInstallments: Array.isArray(wrapper?.linked_bonus_installments)
        ? wrapper.linked_bonus_installments
        : normalized?.linkedBonusInstallments || [],
      response: response?.data,
    };
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to load commission invoice."),
    );
  }
}

/** POST /new-commission-invoices/{invoiceId}/update */
export async function updateCommissionInvoice(invoiceId, payload) {
  if (!invoiceId) throw new Error("Invoice id is required.");
  try {
    const { body, isFormData } = buildInvoiceCreateRequestBody(payload);
    const response = await apiUrl.post(
      `/new-commission-invoices/${invoiceId}/update`,
      body,
      ...(isFormData ? [buildMultipartFormDataRequestConfig()] : []),
    );
    if (!isApiSuccess(response)) {
      throw new Error(
        response?.data?.message || "Failed to update commission invoice.",
      );
    }
    const data = unwrapApiData(response?.data) ?? response?.data;
    return normalizeCommissionInvoice(
      data?.invoice || data?.commission_invoice || data,
    );
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to update commission invoice."),
    );
  }
}

/** POST /new-commission-invoices/{invoiceId}/bill-to-print-options */
export async function updateCommissionInvoiceBillToPrintOptions(
  invoiceId,
  billToPrintOptions,
  invoiceDisplayOptions = null,
) {
  if (!invoiceId) throw new Error("Invoice id is required.");
  const body = invoiceDisplayOptions
    ? { invoice_display_options: invoiceDisplayOptions }
    : { bill_to_print_options: billToPrintOptions };
  try {
    const response = await apiUrl.post(
      `/new-commission-invoices/${invoiceId}/bill-to-print-options`,
      body,
    );
    if (!isApiSuccess(response)) {
      throw new Error(
        response?.data?.message || "Failed to save bill-to print options.",
      );
    }
    const data = unwrapApiData(response?.data) ?? response?.data;
    return normalizeCommissionInvoice(
      data?.invoice || data?.commission_invoice || data,
    );
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to save bill-to print options."),
    );
  }
}

/** POST /finance-invoices/{invoiceId}/bill-to-print-options */
export async function updateFinanceInvoiceBillToPrintOptions(
  invoiceId,
  billToPrintOptions,
  invoiceDisplayOptions = null,
) {
  if (!invoiceId) throw new Error("Invoice id is required.");
  const body = invoiceDisplayOptions
    ? { invoice_display_options: invoiceDisplayOptions }
    : { bill_to_print_options: billToPrintOptions };
  try {
    const response = await apiUrl.post(
      `/finance-invoices/${invoiceId}/bill-to-print-options`,
      body,
    );
    if (!isApiSuccess(response)) {
      throw new Error(
        response?.data?.message || "Failed to save bill-to print options.",
      );
    }
    const data = unwrapApiData(response?.data) ?? response?.data;
    return normalizeFinanceInvoiceRecord({ data: { data } });
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to save bill-to print options."),
    );
  }
}

/** POST /new-commission-invoices/{invoiceId}/expected-payment */
export async function updateCommissionInvoiceExpectedPayment(
  invoiceId,
  { expected_payment_date, expected_payment_note } = {},
) {
  if (!invoiceId) throw new Error("Invoice id is required.");

  try {
    const response = await apiUrl.post(
      `/new-commission-invoices/${invoiceId}/expected-payment`,
      {
        expected_payment_date,
        expected_payment_note,
      },
    );

    if (!isApiSuccess(response)) {
      throw new Error(
        response?.data?.message ||
          "Failed to save expected payment details.",
      );
    }

    const data = unwrapApiData(response?.data) ?? response?.data;
    return normalizeCommissionInvoice(
      data?.invoice || data?.commission_invoice || data,
    );
  } catch (error) {
    throw new Error(
      getApiErrorMessage(
        error,
        "Failed to save expected payment details.",
      ),
    );
  }
}

/** POST /new-commission-invoices/{invoiceId}/void */
export async function voidCommissionInvoice(invoiceId) {
  if (!invoiceId) throw new Error("Invoice id is required.");
  try {
    const response = await apiUrl.post(
      `/new-commission-invoices/${invoiceId}/void`,
    );
    if (!isApiSuccess(response)) {
      throw new Error(
        response?.data?.message || "Failed to void commission invoice.",
      );
    }
    return response?.data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to void commission invoice."),
    );
  }
}

/** POST /finance-invoices/{invoiceId}/void */
export async function voidFinanceInvoice(invoiceId) {
  if (!invoiceId) throw new Error("Invoice id is required.");
  try {
    const response = await apiUrl.post(`/finance-invoices/${invoiceId}/void`);
    if (!isApiSuccess(response)) {
      throw new Error(
        response?.data?.message || "Failed to void invoice.",
      );
    }
    return response?.data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to void invoice."),
    );
  }
}

/** POST /new-commission-invoices/{invoiceId}/draft */
export async function moveCommissionInvoiceToDraft(invoiceId) {
  if (!invoiceId) throw new Error("Invoice id is required.");
  try {
    const response = await apiUrl.post(
      `/new-commission-invoices/${invoiceId}/draft`,
    );
    if (!isApiSuccess(response)) {
      throw new Error(
        response?.data?.message ||
          "Failed to move commission invoice to draft.",
      );
    }
    return response?.data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(
        error,
        "Failed to move commission invoice to draft.",
      ),
    );
  }
}

/** POST /finance-invoices/{invoiceId}/draft */
export async function moveFinanceInvoiceToDraft(invoiceId) {
  if (!invoiceId) throw new Error("Invoice id is required.");
  try {
    const response = await apiUrl.post(`/finance-invoices/${invoiceId}/draft`);
    if (!isApiSuccess(response)) {
      throw new Error(
        response?.data?.message || "Failed to move invoice to draft.",
      );
    }
    return response?.data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to move invoice to draft."),
    );
  }
}

/** GET /getProviderDetail/{id} — full provider for commission bill-to */
export async function fetchProviderBillToProfile(providerId) {
  const id = Number(providerId);
  if (!id) return null;
  try {
    const response = await apiUrl.get(`/getProviderDetail/${id}`);
    const detail = response?.data?.data?.detail;
    const persondetail = response?.data?.data?.persondetail;
    if (!detail || typeof detail !== "object") return null;
    return buildProviderDisplayProfileFromApi(
      { ...detail, id },
      Array.isArray(persondetail) ? persondetail : [],
    );
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to load provider billing details."),
    );
  }
}

/** POST /updateProvider/{providerId} — rename provider (full payload from getProviderDetail). */
export async function updateCommissionProviderName(providerId, name) {
  const id = Number(providerId);
  if (!id) throw new Error("Provider id is required.");
  const trimmed = String(name || "").trim();
  if (!trimmed) throw new Error("Provider name is required.");

  try {
    const response = await apiUrl.get(`/getProviderDetail/${id}`);
    const detail = response?.data?.data?.detail;
    const persondetail = response?.data?.data?.persondetail;
    if (!detail || typeof detail !== "object") {
      throw new Error("Provider details could not be loaded.");
    }

    const personList = Array.isArray(persondetail) ? persondetail : [];
    const payload = {
      name: trimmed,
      country: detail.country,
      city: detail.city,
      address: String(detail.address || "").trim(),
      categoryid: detail.categoryid,
      agreement_startdate: detail.agreementstartdate,
      agreement_enddate: detail.agreementenddate,
      designation: detail.designation || "",
      person_contact_list: personList.map((row) => ({
        name: row.name || "",
        email: row.email || "",
        mobile: row.mobile || "",
        landline: row.landline || "",
        designation: row.designation || "",
      })),
    };

    const updateResponse = await apiUrl.post(`updateProvider/${id}`, payload);
    if (updateResponse?.data?.error) {
      throw new Error(
        updateResponse.data.error ||
          updateResponse.data.message ||
          "Failed to update provider.",
      );
    }
    return updateResponse?.data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to update provider name."),
    );
  }
}

/** POST /updateProviderAddress/{providerId} — billing address only */
export async function updateProviderBillingAddress(providerId, address) {
  if (!providerId) throw new Error("Provider id is required.");
  try {
    const response = await apiUrl.post(`/updateProviderAddress/${providerId}`, {
      address: String(address || "").trim(),
    });
    if (response?.data?.status === false || response?.data?.error) {
      throw new Error(
        response?.data?.error ||
          response?.data?.message ||
          "Failed to update provider address.",
      );
    }
    return response?.data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to update provider address."),
    );
  }
}

/** POST /new-commission-invoices/{invoiceId}/delete */
export async function deleteCommissionInvoice(invoiceId) {
  if (!invoiceId) throw new Error("Invoice id is required.");
  try {
    const response = await apiUrl.post(
      `/new-commission-invoices/${invoiceId}/delete`,
    );
    if (!isApiSuccess(response)) {
      throw new Error(
        response?.data?.message || "Failed to delete commission invoice.",
      );
    }
    return response?.data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to delete commission invoice."),
    );
  }
}

export function normalizeCommissionInquiryFeeRecord(raw = {}) {
  const data =
    raw && typeof raw === "object" && raw.data && typeof raw.data === "object"
      ? raw.data
      : raw || {};

  const readNumber = (...keys) => {
    for (const key of keys) {
      const value = data?.[key];
      if (value != null && value !== "") {
        const num = Number(value);
        if (Number.isFinite(num)) return num;
      }
    }
    return 0;
  };

  const readOptionalNumber = (...keys) => {
    for (const key of keys) {
      const value = data?.[key];
      if (value != null && value !== "") {
        const num = Number(value);
        if (Number.isFinite(num)) return num;
      }
    }
    return null;
  };

  return {
    application_fee: readNumber("application_fee", "applicationFee"),
    application_fee_paid: readNumber(
      "application_fee_paid",
      "applicationFeePaid",
    ),
    application_fee_invoiced: readNumber(
      "application_fee_invoiced",
      "applicationFeeInvoiced",
    ),
    application_fee_remaining: readOptionalNumber(
      "application_fee_remaining",
      "applicationFeeRemaining",
    ),
    application_fee_status:
      data.application_fee_status || data.applicationFeeStatus || "",
    material_fee: readNumber("material_fee", "materialFee"),
    material_fee_paid: readNumber("material_fee_paid", "materialFeePaid"),
    material_fee_invoiced: readNumber(
      "material_fee_invoiced",
      "materialFeeInvoiced",
    ),
    material_fee_remaining: readOptionalNumber(
      "material_fee_remaining",
      "materialFeeRemaining",
    ),
    material_fee_status:
      data.material_fee_status || data.materialFeeStatus || "",
    currency: data.currency || "AUD",
  };
}

/** GET /commission-inquiry-fees */
export async function fetchCommissionInquiryFees({
  applicationId,
  clientId,
  providerId,
  applicationCourseId,
} = {}) {
  try {
    const response = await apiUrl.get("/commission-inquiry-fees", {
      params: {
        application_id: applicationId,
        client_id: clientId,
        provider_id: providerId,
        application_course_id: applicationCourseId || undefined,
      },
    });

    if (response?.data?.success === false) {
      throw new Error(
        response?.data?.message || "Failed to fetch inquiry fee record.",
      );
    }

    return normalizeCommissionInquiryFeeRecord(
      response?.data?.data || response?.data || {},
    );
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch inquiry fee record.";
    throw new Error(message);
  }
}

/** POST /commission-inquiry-fees/upsert */
export async function upsertCommissionInquiryFees(payload = {}) {
  try {
    const response = await apiUrl.post("/commission-inquiry-fees/upsert", {
      application_id: Number(payload.application_id),
      client_id: Number(payload.client_id),
      provider_id: Number(payload.provider_id),
      application_course_id:
        payload.application_course_id != null &&
        payload.application_course_id !== ""
          ? Number(payload.application_course_id)
          : undefined,
      application_fee:
        payload.application_fee != null && payload.application_fee !== ""
          ? Number(payload.application_fee)
          : 0,
      application_fee_paid:
        payload.application_fee_paid != null &&
        payload.application_fee_paid !== ""
          ? Number(payload.application_fee_paid)
          : 0,
      material_fee:
        payload.material_fee != null && payload.material_fee !== ""
          ? Number(payload.material_fee)
          : 0,
      material_fee_paid:
        payload.material_fee_paid != null && payload.material_fee_paid !== ""
          ? Number(payload.material_fee_paid)
          : 0,
      currency: payload.currency || "AUD",
      notes: payload.notes || null,
    });

    if (response?.data?.success === false) {
      throw new Error(
        response?.data?.message || "Failed to save inquiry fee record.",
      );
    }

    return response?.data?.data || null;
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to save inquiry fee record.";
    throw new Error(message);
  }
}

/** POST /api/v1/updateApplicationStudentCourseDetails */
export async function updateApplicationStudentCourseDetails(payload) {
  const body = {
    application_course_id: payload?.application_course_id ?? null,
    application_id: String(payload?.application_id ?? ""),
    client_id: String(payload?.client_id ?? ""),
    course_id: String(payload?.course_id ?? ""),
    course_name: String(payload?.course_name ?? ""),
    intake: String(payload?.intake ?? ""),
    intake_id:
      payload?.intake_id != null && payload?.intake_id !== ""
        ? String(payload.intake_id)
        : undefined,
    dob: String(payload?.dob ?? ""),
    end_date: String(payload?.end_date ?? ""),
    provider_id: String(payload?.provider_id ?? ""),
    start_date: String(payload?.start_date ?? ""),
    student_id: String(payload?.student_id ?? ""),
    tuition_fee: String(payload?.tuition_fee ?? ""),
    commissionable_tuition_fee: String(
      payload?.commissionable_tuition_fee ?? "",
    ),
    type: String(payload?.type || "education"),
  };

  try {
    const response = await apiUrl.post(
      "/updateApplicationStudentCourseDetails",
      body,
    );

    if (response?.data?.status || response?.data?.success || response?.status) {
      return response.data;
    }

    const message =
      response?.data?.message || "Failed to update student course details.";
    throw new Error(message);
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to update student course details.";
    throw new Error(message);
  }
}

export async function fetchIntakes() {
  const response = await apiUrl.get("/fetchIntakes");
  if (response?.status) {
    return response?.data?.data || [];
  }
  return [];
}

export async function fetchProviders(type) {
  const category = getProviderCategory(type);
  if (!category) return [];
  try {
    const response = await apiUrl(`/getProvidersByNames/${category}`);
    if (response?.data?.status) {
      return Array.isArray(response?.data?.data) ? response.data.data : [];
    }
  } catch (error) {
    console.error(error);
  }
  return [];
}

function normalizeExternalAgentOptions(items = []) {
  return (Array.isArray(items) ? items : []).map((item) => ({
    ...item,
    id: item?.id,
    name: item.agencyname || item.name || item.title || `Agent ${item.id}`,
    agencyname: item.agencyname || item.name || item.title || "",
  }));
}

/** GET getExternalAgents — processing and referring external agents */
export async function fetchExternalAgents() {
  try {
    const response = await apiUrl.get("/getExternalAgents");
    if (response?.data?.status && response?.data?.data) {
      const data = response.data.data;
      return {
        processing: normalizeExternalAgentOptions(data.processing),
        referring: normalizeExternalAgentOptions(data.referring),
      };
    }
  } catch (error) {
    console.error(error);
  }
  return { processing: [], referring: [] };
}

export async function fetchBranches() {
  try {
    const response = await apiUrl.get("/getBranches");
    if (response?.data?.status) {
      return Array.isArray(response?.data?.data) ? response.data.data : [];
    }
  } catch (error) {
    console.error(error);
  }
  return [];
}

export async function fetchCoursesForProvider(providerId) {
  if (!providerId) return [];
  try {
    const response = await apiUrl.get(`/getCourseByNames/${providerId}`);
    if (response?.data?.status) {
      return Array.isArray(response?.data?.data) ? response.data.data : [];
    }
  } catch (error) {
    console.error(error);
  }
  return [];
}

export async function fetchPackagesForProvider(providerId) {
  if (!providerId) return [];
  try {
    const response = await apiUrl.get(`/getPackagesByNames/${providerId}`);
    if (response?.data?.status) {
      return Array.isArray(response?.data?.data) ? response.data.data : [];
    }
  } catch (error) {
    console.error(error);
  }
  return [];
}

export async function fetchCourseIntakes(courseId, providerId) {
  if (!courseId || !providerId) return [];
  try {
    const response = await apiUrl.get(
      `/courseIntakes/${courseId}/${providerId}`,
    );
    if (response?.data?.status) {
      return Array.isArray(response?.data?.data) ? response.data.data : [];
    }
  } catch (error) {
    console.error(error);
  }
  return [];
}

export async function fetchCoursesForProviders(providerIds = []) {
  if (!providerIds.length) return [];
  const ids = providerIds.map((p) => p.id ?? p);
  if (ids.length === 1) {
    return fetchCoursesForProvider(ids[0]);
  }
  const results = await mapWithConcurrency(ids, (id) =>
    fetchCoursesForProvider(id),
  );
  const merged = new Map();
  results.flat().forEach((course) => {
    if (course?.id != null) merged.set(course.id, course);
  });
  return Array.from(merged.values());
}

export function buildApplicationQueuePayload(application, queueType) {
  const clientId = application?.clientid;
  const applicationId = application?.application_id;
  const applicationType = application?.category;

  if (!clientId || !applicationId || !applicationType) {
    throw new Error("Missing required application details.");
  }

  const payload = {
    client_id: Number(clientId),
    application_id: Number(applicationId),
    application_type: applicationType,
    queue_type: queueType,
  };

  if (application?.providerid != null && application.providerid !== "") {
    payload.provider_id = Number(application.providerid);
  }

  if (application?.courseid != null && application.courseid !== "") {
    payload.course_id = Number(application.courseid);
  }

  return payload;
}

/** POST /application-queues */
export async function addApplicationToQueue(application, queueType) {
  try {
    const payload = buildApplicationQueuePayload(application, queueType);
    const response = await apiUrl.post("/application-queues", payload);

    if (response?.data?.status) {
      return response.data;
    }

    throw new Error(
      response?.data?.message || "Failed to add application to queue.",
    );
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to add application to queue.";
    throw new Error(message);
  }
}

export async function addApplicationsToQueue(applications = [], queueType) {
  if (!applications.length) {
    throw new Error("No applications selected.");
  }

  const results = await mapWithConcurrency(applications, (application) =>
    addApplicationToQueue(application, queueType),
  );

  return results;
}

export function resolveApplicationCourseId(application = {}) {
  const raw =
    application?.application_course_id ??
    application?.applicationcourseid ??
    application?.eduappcourse_id ??
    null;

  if (raw == null || String(raw).trim() === "") {
    return null;
  }

  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/** True when this commission row is one course inside a multi-course package. */
export function isPackageCourseRow(application = {}) {
  if (application?.category !== "education") {
    return false;
  }

  const courses = application?.eduappcourses;
  if (Array.isArray(courses) && courses.length > 1) {
    return resolveApplicationCourseId(application) != null;
  }

  return Boolean(application?.packageid && resolveApplicationCourseId(application));
}

/** POST /commission-workflow/apply-course-outcome */
export async function applyCommissionCourseOutcome(
  application,
  { status, statusText } = {},
) {
  const applicationId = application?.application_id;
  const applicationType = application?.category || "education";
  const applicationCourseId = resolveApplicationCourseId(application);

  if (!applicationId) {
    throw new Error("Missing application id.");
  }
  if (!applicationCourseId) {
    throw new Error("Missing application course id.");
  }

  try {
    const response = await apiUrl.post("/commission-workflow/apply-course-outcome", {
      application_id: Number(applicationId),
      application_type: applicationType,
      application_course_id: applicationCourseId,
      status_value: Number(status),
      status_title: statusText,
    });

    if (response?.data?.status) {
      return response.data;
    }

    throw new Error(
      response?.data?.message || "Failed to update course workflow.",
    );
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to update course workflow.";
    throw new Error(message);
  }
}

/**
 * Mark an application Discontinued using the same endpoints as the
 * application detail status dropdown (updateEduAppStatus / updateMigAppStatus).
 * Package course rows use course-scoped commission workflow instead.
 */
export async function discontinueApplication(
  application,
  { notes, statusChangeDate } = {},
) {
  const appId = application?.application_id;
  const category = application?.category || "education";
  const trimmedNotes = String(notes || "").trim();

  if (!appId) {
    throw new Error("Missing application id.");
  }
  if (!trimmedNotes) {
    throw new Error("Please enter a note.");
  }

  const { status, statusText } = getDiscontinuedStatusMeta(category);
  const appName =
    application?.coursename || application?.packagename || "Application";

  if (category === "migration") {
    const response = await apiUrl.post("/updateMigAppStatus", {
      appId,
      status,
      statusText,
      notes: trimmedNotes,
      course: application?.course || application?.coursename || "visa",
      appName,
    });

    if (response?.data?.error) {
      throw new Error(response.data.error);
    }
    if (response?.data?.status || response?.data?.message) {
      return response.data;
    }
    throw new Error("Failed to discontinue application.");
  }

  if (isPackageCourseRow(application)) {
    return applyCommissionCourseOutcome(application, { status, statusText });
  }

  const response = await apiUrl.post("/updateEduAppStatus", {
    appId,
    status,
    statusText,
    notes: trimmedNotes,
    status_change_date: statusChangeDate || null,
    appname: appName,
  });

  if (response?.data?.error) {
    throw new Error(response.data.error);
  }
  if (response?.data?.status || response?.data?.message) {
    return response.data;
  }

  throw new Error("Failed to discontinue application.");
}

/**
 * Restore a discontinued commission row back to Active.
 * Package courses use course-scoped workflow; single apps use updateEduAppStatus.
 */
export async function reactivateApplication(
  application,
  { notes, statusChangeDate } = {},
) {
  const appId = application?.application_id;
  const category = application?.category || "education";
  const { status, statusText } = getReactivateStatusMeta(application);
  const trimmedNotes = String(notes || "").trim();
  const appName =
    application?.coursename || application?.packagename || "Application";

  if (!appId) {
    throw new Error("Missing application id.");
  }

  if (isPackageCourseRow(application)) {
    return applyCommissionCourseOutcome(application, { status, statusText });
  }

  const noteText =
    trimmedNotes ||
    `Restored to ${statusText} from Commissionable Students on ${new Date().toISOString().slice(0, 10)}.`;

  if (category === "migration") {
    const response = await apiUrl.post("/updateMigAppStatus", {
      appId,
      status,
      statusText,
      notes: noteText,
      course: application?.course || application?.coursename || "visa",
      appName,
    });

    if (response?.data?.error) {
      throw new Error(response.data.error);
    }
    if (response?.data?.status || response?.data?.message) {
      return response.data;
    }
    throw new Error("Failed to restore application to Active.");
  }

  const response = await apiUrl.post("/updateEduAppStatus", {
    appId,
    status,
    statusText,
    notes: noteText,
    status_change_date: statusChangeDate || new Date().toISOString().slice(0, 10),
    appname: appName,
  });

  if (response?.data?.error) {
    throw new Error(response.data.error);
  }
  if (response?.data?.status || response?.data?.message) {
    return response.data;
  }

  throw new Error("Failed to restore application to Active.");
}

/** POST /commission-workflow/mark-completed */
export async function markCommissionStudentCompleted(application) {
  const applicationId = application?.application_id;
  const applicationType = application?.category || "education";
  const applicationCourseId = resolveApplicationCourseId(application);

  if (!applicationId) {
    throw new Error("Missing application id.");
  }

  try {
    const payload = {
      application_id: Number(applicationId),
      application_type: applicationType,
    };

    if (applicationCourseId) {
      payload.application_course_id = applicationCourseId;
    }

    const response = await apiUrl.post("/commission-workflow/mark-completed", payload);

    if (response?.data?.status) {
      return response.data;
    }

    throw new Error(
      response?.data?.message || "Failed to mark student as completed.",
    );
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to mark student as completed.";
    throw new Error(message);
  }
}

/** POST /commission-workflow/revert-completed */
export async function revertCommissionStudentCompleted(application) {
  const applicationId = application?.application_id;
  const applicationType = application?.category || "education";
  const applicationCourseId = resolveApplicationCourseId(application);

  if (!applicationId) {
    throw new Error("Missing application id.");
  }

  try {
    const payload = {
      application_id: Number(applicationId),
      application_type: applicationType,
    };

    if (applicationCourseId) {
      payload.application_course_id = applicationCourseId;
    }

    const response = await apiUrl.post(
      "/commission-workflow/revert-completed",
      payload,
    );

    if (response?.data?.status) {
      return response.data;
    }

    throw new Error(
      response?.data?.message || "Failed to move student back to Active.",
    );
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to move student back to Active.";
    throw new Error(message);
  }
}

function buildCommissionWorkflowPayload(application) {
  const applicationId = application?.application_id;
  const applicationType = application?.category || "education";
  const applicationCourseId = resolveApplicationCourseId(application);

  if (!applicationId) {
    throw new Error("Missing application id.");
  }

  const payload = {
    application_id: Number(applicationId),
    application_type: applicationType,
  };

  if (applicationCourseId) {
    payload.application_course_id = applicationCourseId;
  }

  return payload;
}

async function postCommissionWorkflow(path, application, fallbackMessage) {
  try {
    const response = await apiUrl.post(path, buildCommissionWorkflowPayload(application));

    if (response?.data?.status) {
      return response.data;
    }

    throw new Error(response?.data?.message || fallbackMessage);
  } catch (error) {
    throw new Error(
      error?.response?.data?.message || error?.message || fallbackMessage,
    );
  }
}

/** POST /commission-workflow/submit-final-review */
export async function submitCommissionFinalReview(application) {
  return postCommissionWorkflow(
    "/commission-workflow/submit-final-review",
    application,
    "Failed to send for final review.",
  );
}

/** POST /commission-workflow/approve-final-review */
export async function approveCommissionFinalReview(application) {
  return postCommissionWorkflow(
    "/commission-workflow/approve-final-review",
    application,
    "Failed to approve final review.",
  );
}

/** POST /commission-workflow/return-from-final-review */
export async function returnCommissionFromFinalReview(application) {
  return postCommissionWorkflow(
    "/commission-workflow/return-from-final-review",
    application,
    "Failed to return from final review.",
  );
}

/** POST /commission-workflow/bulk */
export async function runCommissionBulkWorkflow({
  action,
  applications = [],
  commissionView = "active",
  notes = null,
  statusChangeDate = null,
} = {}) {
  if (!action) {
    throw new Error("Missing bulk action.");
  }

  const items = (applications || []).map((application) => {
    const applicationId = application?.application_id;
    if (!applicationId) {
      throw new Error("Missing application id on a selected row.");
    }

    const applicationCourseId = resolveApplicationCourseId(application);
    const payload = {
      application_id: Number(applicationId),
      application_type: application?.category || "education",
    };

    if (applicationCourseId) {
      payload.application_course_id = applicationCourseId;
    }
    if (application?.queue_id) {
      payload.queue_id = Number(application.queue_id);
    }

    return payload;
  });

  try {
    const response = await apiUrl.post("/commission-workflow/bulk", {
      action,
      commission_view: commissionView,
      items,
      notes: notes || undefined,
      status_change_date: statusChangeDate || undefined,
    });

    if (response?.data?.status) {
      return response.data;
    }

    throw new Error(response?.data?.message || "Bulk action failed.");
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Bulk action failed.";
    throw new Error(message);
  }
}

/** @deprecated Use removeApplicationFromQueue — same POST /application-queues/{id}/delete */
export async function removeApplicationCommissionable(application) {
  const queueId = application?.queue_id;
  if (!queueId) {
    throw new Error("Queue record not found for this application.");
  }
  return removeApplicationFromQueue(queueId);
}

/** POST /application-queues/{id}/delete — move workflow back to general */
export async function removeApplicationFromQueue(queueId) {
  if (!queueId) {
    throw new Error("Queue record not found.");
  }

  try {
    const response = await apiUrl.post(`/application-queues/${queueId}/delete`);

    if (response?.data?.status) {
      return response.data;
    }

    throw new Error(
      response?.data?.message || "Failed to remove application from queue.",
    );
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to remove application from queue.";
    throw new Error(message);
  }
}

export function exportApplicationsCsv(applications) {
  const headers = [
    "Client Name",
    "Processing Agent",
    "Referring Agent",
    "Processing Branch",
    "Referring Branch",
    "Provider",
    "Course/Package",
    "Status",
    "Intake",
    "Last Updated",
  ];
  const latestStatus = (app) => {
    const history = app?.status_history;
    if (Array.isArray(history) && history.length > 0) {
      return history[history.length - 1]?.status_title || "";
    }
    return app?.history_status_title || app?.status_title || "";
  };
  const rows = applications.map((app) => [
    app.clientname || "",
    resolveApplicationAgentDisplay(app, "processing"),
    resolveApplicationAgentDisplay(app, "referring"),
    resolveApplicationBranchDisplay(app, "processing"),
    resolveApplicationBranchDisplay(app, "referring"),
    app.providername || "",
    app.coursename || app.packagename || "",
    latestStatus(app),
    app.intake || "",
    app.statuschangedate || "",
  ]);
  const escape = (v) => `"${String(v).replace(/"/g, '""')}"`;
  const csv = [headers, ...rows]
    .map((row) => row.map(escape).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `commissions-report-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

/** GET /new-commissions — read-only list for finance installment queue UI. */
export async function fetchCommissionPlansIndex(params = {}) {
  const response = await apiUrl.get("/new-commissions", { params });
  if (response?.data?.success === false) {
    throw new Error(
      response?.data?.message || "Failed to fetch commission plans.",
    );
  }
  const data = response?.data?.data ?? response?.data;
  return Array.isArray(data) ? data : [];
}

function mapCommissionInstallmentQueueRow(row = {}) {
  const kind = row.installment_kind ?? row.installmentKind ?? "commission";
  return {
    id: row.id,
    installmentKind: kind,
    planId: row.plan_id ?? row.planId,
    installmentNo: row.installment_no ?? row.installmentNo,
    dueDate: row.due_date ?? row.dueDate,
    clientName:
      row.client_name ??
      row.clientName ??
      row.student_name ??
      row.studentName ??
      "",
    studentName:
      row.client_name ??
      row.clientName ??
      row.student_name ??
      row.studentName ??
      "",
    applicationName: row.application_name ?? row.applicationName ?? "",
    clientId: row.client_id ?? row.clientId,
    applicationId: row.application_id ?? row.applicationId,
    feeDue: Number(row.fee_due ?? row.feeDue) || 0,
    commissionDue: Number(row.commission_due ?? row.commissionDue) || 0,
    currency: row.currency || "AUD",
    detailPath: row.detail_path ?? row.detailPath ?? null,
  };
}

/** GET /new-commission-installments/queue — paginated finance installments queue. */
export async function fetchCommissionInstallmentsQueue(params = {}) {
  const response = await apiUrl.get("/new-commission-installments/queue", {
    params: {
      page: params.page ?? 1,
      per_page: params.perPage ?? 10,
      due_filter: params.dueFilter ?? "all",
      due_from: params.dueFrom || undefined,
      due_to: params.dueTo || undefined,
      search: params.search?.trim() || undefined,
      queue_type: params.queueType ?? "commission",
    },
  });

  if (response?.data?.success === false) {
    throw new Error(
      response?.data?.message || "Failed to fetch installments queue.",
    );
  }

  const body = response?.data ?? {};
  const rows = Array.isArray(body.data) ? body.data : [];
  const pagination = body.pagination ?? {};

  return {
    data: rows.map(mapCommissionInstallmentQueueRow),
    pagination: {
      currentPage: Number(pagination.currentPage) || 1,
      perPage: Number(pagination.perPage) || params.perPage || 25,
      total: Number(pagination.total) || 0,
      lastPage: Number(pagination.lastPage) || 1,
    },
    meta: {
      awaitingTotal: Number(body.meta?.awaiting_total) || 0,
      awaitingCommission: Number(body.meta?.awaiting_commission) || 0,
      awaitingBonus: Number(body.meta?.awaiting_bonus) || 0,
    },
  };
}
